import { useEffect, useRef, useState } from "react";

import GovernanceHistory from "../../components/director/governance/GovernanceHistory";
import { availableDiscoveredProjects, mergeDiscoveredEntities } from "../../components/director/governance/mergeDiscoveredEntitiesAndProjects";
import PortfolioMappingForm from "../../components/director/governance/PortfolioMappingForm";
import ResourceMatchReview from "../../components/director/governance/ResourceMatchReview";
import {
  discoverGovernancePortfolio,
  fetchGovernanceAudit,
  fetchGovernancePortfolio,
  updateGovernancePortfolio,
} from "./financialGovernanceApi";
import { fetchProductAccessCompanies } from "./productAccessApi";

let clientSequence = 0;
const clientKey = (prefix) => `${prefix}-${clientSequence += 1}`;

const defaultScenarios = () => ({
  base: { inflowDelayDays: 0, futureInflowHaircutBps: 0, uncommittedOutflowUpliftBps: 0 },
  conservative: { inflowDelayDays: 30, futureInflowHaircutBps: 0, uncommittedOutflowUpliftBps: 0 },
  stress: { inflowDelayDays: 30, futureInflowHaircutBps: 1000, uncommittedOutflowUpliftBps: 500 },
});

const emptyPortfolio = () => ({
  name: "Novo portfólio",
  state: "active",
  legalEntities: [],
  projects: [],
  riskPolicy: { minimumCashBufferCentsByPoolingGroup: {} },
  cashflowScenarios: defaultScenarios(),
});

const decorate = (portfolio) => ({
  ...portfolio,
  legalEntities: (portfolio.legalEntities || []).map((entity) => ({ ...entity, _clientKey: clientKey("entity") })),
  projects: (portfolio.projects || []).map((project) => ({
    ...project,
    _clientKey: clientKey("project"),
    costCenters: (project.costCenters || []).map((center) => ({ ...center, _clientKey: clientKey("center") })),
  })),
});

const sanitize = (portfolio) => {
  const legalEntities = portfolio.legalEntities.map(({ _clientKey, ...entity }) => ({
    ...entity,
    poolingGroupId: entity.poolingGroupId || (entity.erpCompanyId > 0 ? `entity-${entity.erpCompanyId}` : ""),
  }));
  const projects = portfolio.projects.map(({ _clientKey, costCenters, ...project }) => ({
    ...project,
    costCenters: costCenters.map(({ _clientKey: _centerKey, ...center }) => center),
  }));
  const groups = [...new Set(legalEntities
    .filter((item) => item.state === "active")
    .map((item) => item.poolingGroupId)
    .filter(Boolean))];
  const buffers = {
    ...(portfolio.riskPolicy?.minimumCashBufferCentsByPoolingGroup || {}),
  };
  groups.forEach((group) => {
    if (buffers[group] === undefined) buffers[group] = 0;
  });
  return {
    name: portfolio.name,
    state: portfolio.state,
    legalEntities,
    projects,
    riskPolicy: {
      ...(portfolio.riskPolicy || {}),
      minimumCashBufferCentsByPoolingGroup: buffers,
    },
    cashflowScenarios: portfolio.cashflowScenarios,
  };
};

const materialSignature = (portfolio) => ({
  entities: portfolio.legalEntities.filter((item) => item.state === "active").map((item) => item.erpCompanyId).sort(),
  projects: portfolio.projects.filter((item) => item.state === "active").map((item) => JSON.stringify({
    key: item.key,
    erpProjectId: item.erpProjectId,
    legalEntityId: item.legalEntityId,
    buildingId: item.buildingId || null,
    wbsCodes: [...(item.wbsCodes || [])].sort(),
  })).sort(),
  centers: portfolio.projects.filter((item) => item.state === "active").flatMap((project) => (
    project.costCenters.map((center) => `${project.key}:${center.id}:${center.role}:${center.cashEffect}`)
  )).sort(),
});

const materialSummary = (before, after) => {
  if (!before) return "Criação do primeiro mapeamento financeiro.";
  const prior = materialSignature(before);
  const next = materialSignature(after);
  const changes = [];
  if (JSON.stringify(prior.entities) !== JSON.stringify(next.entities)) changes.push(`Entidades ativas: ${prior.entities.join(", ") || "nenhuma"} → ${next.entities.join(", ") || "nenhuma"}`);
  if (JSON.stringify(prior.projects) !== JSON.stringify(next.projects)) changes.push("Projetos ativos ou seus vínculos financeiros foram alterados.");
  if (JSON.stringify(prior.centers) !== JSON.stringify(next.centers)) changes.push("Centros ativos, papéis ou efeitos de caixa foram alterados.");
  return changes.join("\n");
};

const FinancialGovernance = () => {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [portfolio, setPortfolio] = useState(null);
  const [originalPortfolio, setOriginalPortfolio] = useState(null);
  const [expectedVersion, setExpectedVersion] = useState(0);
  const [history, setHistory] = useState([]);
  const [reason, setReason] = useState("");
  const [sourceNote, setSourceNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [discovered, setDiscovered] = useState(null);
  const [discovering, setDiscovering] = useState(false);
  const [discoverError, setDiscoverError] = useState(null);
  const requestRef = useRef(0);
  const companyIdRef = useRef(companyId);
  companyIdRef.current = companyId;

  const decorateNewRows = (next) => ({
    ...next,
    legalEntities: next.legalEntities.map((item) => (
      item._clientKey ? item : { ...item, _clientKey: clientKey("entity") }
    )),
    projects: next.projects.map((item) => (
      item._clientKey ? item : { ...item, _clientKey: clientKey("project") }
    )),
  });

  const runDiscovery = async () => {
    const requestedCompanyId = companyId;
    setDiscovering(true);
    setDiscoverError(null);
    try {
      const result = await discoverGovernancePortfolio(requestedCompanyId);
      if (companyIdRef.current !== requestedCompanyId) return;
      setDiscovered(result);
      setPortfolio((current) => decorateNewRows(mergeDiscoveredEntities(current, result)));
    } catch (requestError) {
      if (companyIdRef.current !== requestedCompanyId) return;
      setDiscoverError(requestError);
    } finally {
      setDiscovering(false);
    }
  };

  const addDiscoveredProject = (project) => {
    setPortfolio((current) => ({
      ...current,
      projects: [...current.projects, {
        _clientKey: clientKey("project"),
        key: `sienge-project-${project.erpProjectId}`,
        erpProjectId: project.erpProjectId,
        name: project.name,
        legalEntityId: project.erpCompanyId,
        buildingId: project.suggestedBuildingId || null,
        wbsCodes: [],
        costCenters: [],
        state: "active",
      }],
    }));
  };

  const loadCompany = async (selectedCompanyId) => {
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setLoading(true);
    setError(null);
    try {
      const [portfolioPayload, auditPayload] = await Promise.all([
        fetchGovernancePortfolio(selectedCompanyId),
        fetchGovernanceAudit(selectedCompanyId),
      ]);
      if (requestRef.current !== requestId) return;
      const decorated = portfolioPayload.portfolio ? decorate(portfolioPayload.portfolio) : null;
      setPortfolio(decorated);
      setOriginalPortfolio(portfolioPayload.portfolio || null);
      setExpectedVersion(portfolioPayload.expectedVersion ?? 0);
      setHistory(auditPayload.items || []);
    } catch (requestError) {
      if (requestRef.current === requestId) setError(requestError);
    } finally {
      if (requestRef.current === requestId) setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    fetchProductAccessCompanies()
      .then((payload) => {
        if (!active) return;
        const available = (payload.companies || []).filter((company) => company.state !== "inactive");
        setCompanies(available);
        setCompanyId(available[0]?._id || "");
        if (available.length === 0) setLoading(false);
      })
      .catch((requestError) => { if (active) { setError(requestError); setLoading(false); } });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    setDiscovered(null);
    setDiscoverError(null);
    if (companyId) loadCompany(companyId);
  }, [companyId]);

  const savePortfolio = async () => {
    if (!portfolio || !reason.trim() || !sourceNote.trim() || saving) return;
    const clean = sanitize(portfolio);
    const summary = materialSummary(originalPortfolio, clean);
    if (summary && !window.confirm(`Esta alteração muda o escopo financeiro:\n\n${summary}\n\nConfirma a gravação?`)) return;
    setSaving(true);
    setError(null);
    try {
      await updateGovernancePortfolio(companyId, {
        expectedVersion,
        reason: reason.trim(),
        sourceNote: sourceNote.trim(),
        portfolio: clean,
      });
      setNotice("Governança salva. A Visão Diretoria ficará pendente até a próxima materialização bem-sucedida.");
      setReason("");
      setSourceNote("");
      await loadCompany(companyId);
    } catch (requestError) {
      if (requestError?.status === 409) {
        await loadCompany(companyId);
        setError(requestError);
      } else setError(requestError);
    } finally {
      setSaving(false);
    }
  };

  const refreshHistory = async () => {
    try {
      const auditPayload = await fetchGovernanceAudit(companyId);
      setHistory(auditPayload.items || []);
    } catch (requestError) {
      setError(requestError);
    }
  };

  return <main className="director-page governance-page">
    <header className="director-page__header">
      <div><span className="director-eyebrow">Backoffice · administração</span><h1>Governança Financeira</h1><p>Mapeamento do portfólio, equivalências e histórico auditável.</p></div>
      <div className="director-filter-field"><label htmlFor="governance-company">Empresa</label><select id="governance-company" value={companyId} onChange={(event) => setCompanyId(event.target.value)}>{companies.map((company) => <option key={company._id} value={company._id}>{company.name}</option>)}</select></div>
    </header>

    {error && <div className="director-state director-state--error" role="alert">{error.message || "Não foi possível concluir a operação."}</div>}
    {notice && <div className="director-state director-state--success" role="status">{notice}</div>}
    {loading && <div className="director-state" role="status">Carregando governança financeira…</div>}
    {!loading && companyId && !portfolio && <section className="governance-section"><h2>Nenhum portfólio financeiro configurado</h2><p>Crie a estrutura inicial para habilitar o fluxo de caixa da Diretoria.</p><button className="director-button" type="button" onClick={() => setPortfolio(decorate(emptyPortfolio()))}>Criar configuração</button></section>}

    {!loading && portfolio && <>
      <div className="governance-discover">
        <button className="director-button" type="button" disabled={discovering} onClick={runDiscovery}>
          {discovering ? "Descobrindo…" : "Descobrir automaticamente"}
        </button>
        {discoverError && <div className="director-state director-state--error" role="alert">{discoverError.message || "Não foi possível descobrir no Sienge."}</div>}
      </div>
      <PortfolioMappingForm
        portfolio={portfolio}
        onChange={setPortfolio}
        discoveredCostCenters={discovered?.costCenters || []}
        discoveredProjects={availableDiscoveredProjects(portfolio, discovered)}
        onAddDiscoveredProject={addDiscoveredProject}
      />
      <section className="governance-section" aria-labelledby="governance-save-title">
        <header><div><span className="director-eyebrow">Rastreabilidade</span><h2 id="governance-save-title">Justificativa da alteração</h2></div></header>
        <div className="governance-grid governance-grid--two">
          <label>Motivo da alteração<textarea aria-label="Motivo da alteração" value={reason} onChange={(event) => setReason(event.target.value)} /></label>
          <label>Fonte do mapeamento<textarea aria-label="Fonte do mapeamento" value={sourceNote} onChange={(event) => setSourceNote(event.target.value)} /></label>
        </div>
        <button className="director-button" type="button" disabled={saving || discovering} onClick={savePortfolio}>Salvar governança financeira</button>
      </section>

      <ResourceMatchReview companyId={companyId} projects={originalPortfolio?.projects || []} onDecisionComplete={refreshHistory} />
      <GovernanceHistory items={history} />
    </>}
  </main>;
};

export default FinancialGovernance;
